/**
 * Rysowanie arkuszy teczki w pdf-lib, ta sama technika co schemat elektryczny i plan dla straży
 * (`lib/schematJednokreskowy.js`, `lib/pdfPlanuRatowniczego.js`): wektory, czcionka Liberation Sans z polskimi
 * znakami, puste pola do wpisania jako pola formularza PDF.
 *
 * `rysownik` przyjmuje wymiary w mm od lewego górnego rogu arkusza (y w dół) i sam przelicza je na punkty PDF.
 * Elementy wspólne dla wszystkich arkuszy: ramka, tabelka, legenda, łańcuch wymiarów, kierunek północy, strzałka
 * spadku i symbole (moduł, komin, okno dachowe, przeszkoda). Kolejne arkusze teczki (połączenia modułów, schemat,
 * rozmieszczenie urządzeń) korzystają z tych samych funkcji.
 */
import { TEKST_WYMIARU } from './wymiary'

export const PT = 72 / 25.4
const RAD = Math.PI / 180

/** Rozmiary czcionki w punktach. */
export const ROZMIAR = { wymiar: 6.5, opis: 6.8, maly: 6, wartosc: 7.5, naglowek: 9 }
const LINIA_OPISU = 2.9 // mm między wierszami tekstu 6,8 pt

/** Szerokość napisu z cyfr w mm bez czcionki: cyfry Liberation Sans mają po 0,556 em (do rozkładu wymiarów). */
export const szerokoscCyfr = (tekst, rozmiar = ROZMIAR.wymiar) => (String(tekst).length * 0.5562 * rozmiar) / PT

/** Środek ciężkości wierzchołków (wystarcza dla wielokątów wypukłych). */
export const srodekWielokata = (punkty) => [
  punkty.reduce((s, p) => s + p[0], 0) / punkty.length,
  punkty.reduce((s, p) => s + p[1], 0) / punkty.length,
]

/** Wielokąt ściągnięty do środka (`f` = 1 bez zmian). */
export const sciagnij = (punkty, f) => {
  const [cx, cy] = srodekWielokata(punkty)
  return punkty.map(([x, y]) => [cx + (x - cx) * f, cy + (y - cy) * f])
}

/** Przekrój wielokąta wypukłego prostą n·p = t: odcinek [[x1, y1], [x2, y2]] albo null. */
export const przekrojProsta = (punkty, n, t) => {
  const przeciecia = []
  for (let i = 0; i < punkty.length; i += 1) {
    const a = punkty[i]
    const b = punkty[(i + 1) % punkty.length]
    const sa = a[0] * n[0] + a[1] * n[1] - t
    const sb = b[0] * n[0] + b[1] * n[1] - t
    if ((sa > 0) === (sb > 0)) continue
    const k = sa / (sa - sb)
    przeciecia.push([a[0] + (b[0] - a[0]) * k, a[1] + (b[1] - a[1]) * k])
  }
  return przeciecia.length >= 2 ? [przeciecia[0], przeciecia[1]] : null
}

/** Linie przez wielokąt wypukły prostopadłe do kierunku `n` (jednostkowy), co `odstep` mm. */
export const liniePoprzeczne = (punkty, n, odstep) => {
  const s = punkty.map(([x, y]) => x * n[0] + y * n[1])
  const wynik = []
  for (let t = Math.min(...s) + odstep / 2; t < Math.max(...s); t += odstep) {
    const l = przekrojProsta(punkty, n, t)
    if (l) wynik.push(l)
  }
  return wynik
}

/** Kreskowanie wielokąta wypukłego liniami pod 45°: odcinki [[x1, y1], [x2, y2]] co `odstep` mm. */
export const kreskowanie = (punkty, odstep = 1.5) => liniePoprzeczne(punkty, [Math.SQRT1_2, Math.SQRT1_2], odstep)

// Kierunek najdłuższego boku wielokąta (oś pasa), wektor jednostkowy.
const osWielokata = (punkty) => {
  let os = [1, 0]
  let maks = -1
  punkty.forEach((a, i) => {
    const b = punkty[(i + 1) % punkty.length]
    const d = Math.hypot(b[0] - a[0], b[1] - a[1])
    if (d > maks) { maks = d; os = [(b[0] - a[0]) / (d || 1), (b[1] - a[1]) / (d || 1)] }
  })
  return os
}

const okrag = (cx, cy, promien, n = 24) => Array.from({ length: n }, (_, i) => [cx + promien * Math.cos((2 * Math.PI * i) / n), cy + promien * Math.sin((2 * Math.PI * i) / n)])

/**
 * Narzędzia rysowania na stronie pdf-lib w mm. `biblioteki` = { rgb, degrees } z pdf-lib.
 */
export const rysownik = (strona, font, { rgb, degrees }) => {
  const H = strona.getHeight()
  const kolory = {
    czarny: rgb(0, 0, 0), szary: rgb(0.42, 0.42, 0.45), jasny: rgb(0.8, 0.8, 0.82), bialy: rgb(1, 1, 1),
    // Przewody jak na planie dla straży i schemacie połączeń właściciela: DC czerwony, AC niebieski.
    dc: rgb(0.8, 0.1, 0.1), ac: rgb(0.12, 0.36, 0.8),
  }
  const X = (mm) => mm * PT
  const Y = (mm) => H - mm * PT

  const linia = (x1, y1, x2, y2, { grubosc = 0.18, kolor = kolory.czarny, kreski = null } = {}) => strona.drawLine({
    start: { x: X(x1), y: Y(y1) }, end: { x: X(x2), y: Y(y2) }, thickness: grubosc * PT, color: kolor,
    ...(kreski ? { dashArray: kreski.map((k) => k * PT) } : {}),
  })
  const wielokat = (punkty, { grubosc = 0.18, kolor = kolory.czarny, kreski = null, wypelnienie = null, zamknij = true } = {}) => {
    if (!punkty || punkty.length < 2) return
    const d = `${punkty.map(([x, y], i) => `${i ? 'L' : 'M'} ${(x * PT).toFixed(3)} ${(y * PT).toFixed(3)}`).join(' ')}${zamknij ? ' Z' : ''}`
    const o = { x: 0, y: H }
    if (wypelnienie) o.color = wypelnienie
    if (grubosc) {
      o.borderColor = kolor
      o.borderWidth = grubosc * PT
      if (kreski) o.borderDashArray = kreski.map((k) => k * PT)
    }
    strona.drawSvgPath(d, o)
  }
  const prostokat = (x, y, w, h, opcje) => wielokat([[x, y], [x + w, y], [x + w, y + h], [x, y + h]], opcje)
  const szer = (t, rozmiar = ROZMIAR.opis) => font.widthOfTextAtSize(String(t), rozmiar) / PT
  /** Napis od linii bazowej w punkcie (x, y); `obrot` w stopniach przeciwnie do wskazówek zegara. */
  const tekst = (t, x, y, { rozmiar = ROZMIAR.opis, wyrownanie = 'lewo', obrot = 0, kolor = kolory.czarny } = {}) => {
    if (t == null || t === '') return
    const s = String(t)
    const w = font.widthOfTextAtSize(s, rozmiar)
    const dx = wyrownanie === 'srodek' ? -w / 2 : wyrownanie === 'prawo' ? -w : 0
    const a = obrot * RAD
    strona.drawText(s, {
      x: X(x) + dx * Math.cos(a), y: Y(y) + dx * Math.sin(a), size: rozmiar, font, color: kolor,
      ...(obrot ? { rotate: degrees(obrot) } : {}),
    })
  }
  /** Podział tekstu na wiersze o szerokości do `maks` mm. */
  const zawin = (t, rozmiar, maks) => {
    const wiersze = []
    let biezacy = ''
    for (const slowo of String(t || '').split(/\s+/).filter(Boolean)) {
      const proba = biezacy ? `${biezacy} ${slowo}` : slowo
      if (biezacy && szer(proba, rozmiar) > maks) { wiersze.push(biezacy); biezacy = slowo } else biezacy = proba
    }
    if (biezacy) wiersze.push(biezacy)
    return wiersze
  }
  return { strona, kolory, linia, wielokat, prostokat, szer, tekst, zawin, X, Y }
}

/** Puste pole formularza PDF z kreską do wpisania ręcznie (jak w schemacie elektrycznym). y = górna krawędź w mm. */
export const pole = (r, form, font, nazwa, x, y, w, h) => {
  const tf = form.createTextField(nazwa)
  tf.addToPage(r.strona, {
    x: r.X(x), y: r.Y(y + h), width: w * PT, height: h * PT, font,
    textColor: r.kolory.czarny, backgroundColor: undefined, borderColor: undefined, borderWidth: 0,
  })
  tf.setFontSize(ROZMIAR.wartosc)
  r.linia(x + 0.3, y + h - 0.6, x + w - 0.3, y + h - 0.6, { grubosc: 0.1, kolor: r.kolory.szary })
}

/** Ramka arkusza. */
export const rysujRamke = (r, uklad) => r.prostokat(uklad.ramka.x, uklad.ramka.y, uklad.ramka.w, uklad.ramka.h, { grubosc: 0.5 })

/**
 * Tabelka w prawym dolnym rogu: dopisek, tytuł i skala (z numerem arkusza), „Opracował” z datą, „Sprawdził”
 * z numerem teczki. Puste wartości to pola formularza (firma wpisze je później).
 * `t` = { dopisek, tytul, skala, arkusz, opracowal, data, sprawdzil, nrTeczki }, `prefiks` rozróżnia pola arkuszy.
 */
export const rysujTabelke = (r, { form, font }, prostokat, t, prefiks) => {
  const { x, y, w, h } = prostokat
  const wys = [9, 12, 8.5, 8.5]
  const prawa = 30
  const xp = x + w - prawa
  const lewaEtykieta = 16.5
  const prawaEtykieta = 13
  r.prostokat(x, y, w, h, { grubosc: 0.35 })
  let yy = y
  const dopisek = r.zawin(t.dopisek, ROZMIAR.maly, w - 4).slice(0, 2)
  dopisek.forEach((l, i) => r.tekst(l, x + w / 2, yy + (wys[0] - dopisek.length * 2.7) / 2 + 2.2 + i * 2.7, { rozmiar: ROZMIAR.maly, wyrownanie: 'srodek' }))
  yy += wys[0]
  const wiersze = [
    ['Tytuł:', t.tytul, 'Skala:', t.skala, null, null],
    ['Opracował:', t.opracowal, 'Data:', t.data, 'opracowal', null],
    ['Sprawdził:', t.sprawdzil, 'Nr teczki:', t.nrTeczki, 'sprawdzil', 'nr_teczki'],
  ]
  wiersze.forEach(([etL, wL, etP, wP, poleL, poleP], i) => {
    const hr = wys[i + 1]
    r.linia(x, yy, x + w, yy, { grubosc: 0.18 })
    r.linia(x + lewaEtykieta, yy, x + lewaEtykieta, yy + hr, { grubosc: 0.18 })
    r.linia(xp, yy, xp, yy + hr, { grubosc: 0.18 })
    r.linia(xp + prawaEtykieta, yy, xp + prawaEtykieta, yy + hr, { grubosc: 0.18 })
    const linia1 = i === 0 ? yy + 4.6 : yy + hr / 2 + 1.1
    r.tekst(etL, x + 1.2, linia1, { rozmiar: ROZMIAR.maly })
    r.tekst(etP, xp + 1.2, linia1, { rozmiar: ROZMIAR.maly })
    const szerL = xp - x - lewaEtykieta - 2.4
    if (wL) {
      r.zawin(wL, ROZMIAR.wartosc, szerL).slice(0, 2).forEach((l, j) => r.tekst(l, x + lewaEtykieta + 1.2, linia1 + j * 3.6, { rozmiar: ROZMIAR.wartosc }))
    } else if (poleL) pole(r, form, font, `${prefiks}_${poleL}`, x + lewaEtykieta + 0.8, yy + 1, szerL + 0.8, hr - 2)
    if (wP) r.tekst(wP, xp + prawaEtykieta + 1.2, linia1, { rozmiar: ROZMIAR.wartosc })
    else if (poleP) pole(r, form, font, `${prefiks}_${poleP}`, xp + prawaEtykieta + 0.6, yy + 1, prawa - prawaEtykieta - 1.2, hr - 2)
    if (i === 0 && t.arkusz) r.tekst(t.arkusz, xp + prawaEtykieta + 1.2, linia1 + 4.4, { rozmiar: ROZMIAR.maly, kolor: r.kolory.szary })
    yy += hr
  })
}

/* ═══ Symbole ══════════════════════════════════════════════════════════════════════════════════════ */

/** Moduł: obrys z podwójną kreską ramy (`rama` w mm na papierze) i numer, jeśli się mieści. */
export const rysujModul = (r, punkty, { nr = null, rama = 0.4 } = {}) => {
  r.wielokat(punkty, { grubosc: 0.18 })
  const boki = punkty.map((p, i) => Math.hypot(punkty[(i + 1) % 4][0] - p[0], punkty[(i + 1) % 4][1] - p[1]))
  const krotszy = Math.min(...boki)
  if (rama >= 0.18 && krotszy > 4 * rama) r.wielokat(sciagnij(punkty, 1 - (2 * rama) / krotszy), { grubosc: 0.08 })
  if (nr != null) {
    const [cx, cy] = srodekWielokata(punkty)
    const t = String(nr)
    let rozmiar = 6.5
    while (rozmiar > 3.5 && (r.szer(t, rozmiar) > krotszy * 0.8 || (rozmiar * 0.72) / PT > krotszy * 0.6)) rozmiar -= 0.25
    r.tekst(t, cx, cy + (rozmiar * 0.36) / PT, { rozmiar, wyrownanie: 'srodek' })
  }
}

/** Komin jak u właściciela: obrys, mniejszy kwadrat w środku z przekątną i zacienioną połową. */
export const rysujKomin = (r, punkty) => {
  r.wielokat(punkty, { grubosc: 0.25 })
  const w = sciagnij(punkty, 0.6)
  if (w.length >= 3) r.wielokat([w[0], w[1], w[2]], { grubosc: 0, wypelnienie: r.kolory.jasny })
  r.wielokat(w, { grubosc: 0.1 })
  if (w.length >= 3) r.linia(w[0][0], w[0][1], w[2][0], w[2][1], { grubosc: 0.1 })
}

/** Okno dachowe: obrys i rama (drugi obrys w środku, narożniki połączone). */
export const rysujOkno = (r, punkty) => {
  r.wielokat(punkty, { grubosc: 0.25 })
  const w = sciagnij(punkty, 0.78)
  r.wielokat(w, { grubosc: 0.13 })
  punkty.forEach((p, i) => r.linia(p[0], p[1], w[i][0], w[i][1], { grubosc: 0.08 }))
}

/** Przeszkoda ogólna (także nieznany `rodzaj`): obrys z krzyżem. */
export const rysujPrzeszkode = (r, punkty) => {
  r.wielokat(punkty, { grubosc: 0.25 })
  if (punkty.length === 4) {
    r.linia(punkty[0][0], punkty[0][1], punkty[2][0], punkty[2][1], { grubosc: 0.1 })
    r.linia(punkty[1][0], punkty[1][1], punkty[3][0], punkty[3][1], { grubosc: 0.1 })
  }
}

/** Część połaci pod wyższym dachem innego skrzydła: obrys przerywany i kreskowanie. */
export const rysujPodDachem = (r, punkty) => {
  r.wielokat(punkty, { grubosc: 0.13, kreski: [1.2, 0.8] })
  for (const [a, b] of kreskowanie(punkty, 1.6)) r.linia(a[0], a[1], b[0], b[1], { grubosc: 0.08, kolor: r.kolory.szary })
}

/** Strefa bez modułów wokół przeszkody (odstęp). */
export const rysujStrefe = (r, punkty) => r.wielokat(punkty, { grubosc: 0.1, kolor: r.kolory.szary, kreski: [0.9, 0.7] })

/** Granica pola modułów (odstęp od krawędzi połaci): kreska z kropką. */
export const rysujOdstep = (r, punkty) => r.wielokat(punkty, { grubosc: 0.1, kolor: r.kolory.szary, kreski: [2.4, 0.7, 0.3, 0.7] })

/**
 * Pasy na połaci (ława i stopnie kominiarskie, płotek i kratka przeciwśniegowa): obrys i znak wzdłuż osi.
 * `krok` w mm na papierze: odstęp poprzeczek ławy (stopnie co dwa kroki, kratka co pół kroku).
 */
export const rysujPas = (r, rodzaj, punkty, krok = 3) => {
  r.wielokat(punkty, { grubosc: 0.25 })
  const os = osWielokata(punkty)
  const poprzek = [-os[1], os[0]]
  const [cx, cy] = srodekWielokata(punkty)
  const srodkowa = przekrojProsta(punkty, poprzek, cx * poprzek[0] + cy * poprzek[1])
  const linie = (lista, grubosc) => lista.forEach(([a, b]) => r.linia(a[0], a[1], b[0], b[1], { grubosc }))
  if (rodzaj === 'lawa_kominiarska') linie(liniePoprzeczne(punkty, os, Math.max(1.2, krok)), 0.1)
  else if (rodzaj === 'stopnie_kominiarskie') linie(liniePoprzeczne(punkty, os, Math.max(2.4, 2 * krok)), 0.3)
  else if (srodkowa) {
    linie([srodkowa], 0.18)
    if (rodzaj === 'kratka_sniegowa') linie(liniePoprzeczne(punkty, os, Math.max(1, krok / 2)), 0.08)
  }
}

export const PASY = ['lawa_kominiarska', 'stopnie_kominiarskie', 'plotek_sniegowy', 'kratka_sniegowa']

/**
 * Przeszkoda według rodzaju; nieznany rodzaj dostaje symbol ogólny. `linie` (mm na papierze): dodatkowe krawędzie,
 * np. daszek lukarny; `krok`: podziałka pasów.
 */
export const rysujPrzeszkodeRodzaju = (r, rodzaj, punkty, { linie = [], krok } = {}) => {
  const boki = punkty.map((p, i) => Math.hypot(punkty[(i + 1) % punkty.length][0] - p[0], punkty[(i + 1) % punkty.length][1] - p[1]))
  const [cx, cy] = srodekWielokata(punkty)
  const krotszy = Math.min(...boki)
  if (rodzaj === 'komin') rysujKomin(r, punkty)
  else if (rodzaj === 'okno_dachowe') rysujOkno(r, punkty)
  else if (rodzaj === 'pod_dachem') rysujPodDachem(r, punkty)
  else if (PASY.includes(rodzaj)) rysujPas(r, rodzaj, punkty, krok)
  else if (rodzaj === 'wylaz') {
    r.wielokat(punkty, { grubosc: 0.25 })
    r.wielokat(sciagnij(punkty, 0.72), { grubosc: 0.13 })
  } else if (rodzaj === 'wentylacja') {
    r.wielokat(punkty, { grubosc: 0.25 })
    r.wielokat(okrag(cx, cy, krotszy * 0.3), { grubosc: 0.13 })
  } else if (rodzaj === 'antena') {
    r.wielokat(punkty, { grubosc: 0.25 })
    r.wielokat(okrag(cx, cy, krotszy * 0.18), { grubosc: 0, wypelnienie: r.kolory.czarny })
    r.linia(cx - krotszy * 0.4, cy, cx + krotszy * 0.4, cy, { grubosc: 0.1 })
    r.linia(cx, cy - krotszy * 0.4, cx, cy + krotszy * 0.4, { grubosc: 0.1 })
  } else if (rodzaj === 'lukarna') {
    r.wielokat(punkty, { grubosc: 0.25 })
    for (const [a, b] of linie) r.linia(a[0], a[1], b[0], b[1], { grubosc: 0.18 })
  } else rysujPrzeszkode(r, punkty)
}

/** Strzałka z grotem od (x, y) w kierunku [dx, dy] (wektor jednostkowy) o długości `dl` mm. */
export const rysujStrzalke = (r, x, y, [dx, dy], dl, { grubosc = 0.18 } = {}) => {
  const kx = x + dx * dl
  const ky = y + dy * dl
  r.linia(x, y, kx - dx * 1.2, ky - dy * 1.2, { grubosc })
  const px = -dy
  const py = dx
  r.wielokat([[kx, ky], [kx - dx * 1.6 + px * 0.6, ky - dy * 1.6 + py * 0.6], [kx - dx * 1.6 - px * 0.6, ky - dy * 1.6 - py * 0.6]], { grubosc: 0, wypelnienie: r.kolory.czarny })
}

/** Kierunek północy: okrąg, strzałka obrócona o `kat` (stopnie zgodnie z ruchem wskazówek od góry kartki), litera N. */
export const rysujPolnoc = (r, cx, cy, kat, promien = 3.8) => {
  const n = 40
  r.wielokat(Array.from({ length: n }, (_, i) => [cx + promien * Math.cos((2 * Math.PI * i) / n), cy + promien * Math.sin((2 * Math.PI * i) / n)]), { grubosc: 0.13 })
  const d = [Math.sin(kat * RAD), -Math.cos(kat * RAD)]
  const p = [-d[1], d[0]]
  const tip = [cx + d[0] * promien * 0.95, cy + d[1] * promien * 0.95]
  const ogon = [cx - d[0] * promien * 0.95, cy - d[1] * promien * 0.95]
  r.wielokat([tip, [cx + p[0] * 1.1, cy + p[1] * 1.1], ogon, [cx - p[0] * 1.1, cy - p[1] * 1.1]], { grubosc: 0.13 })
  r.wielokat([tip, [cx + p[0] * 1.1, cy + p[1] * 1.1], ogon], { grubosc: 0, wypelnienie: r.kolory.czarny })
  r.tekst('N', cx + d[0] * (promien + 2.4), cy + d[1] * (promien + 2.4) + 1.1, { rozmiar: 7.5, wyrownanie: 'srodek' })
}

/* ═══ Legenda ══════════════════════════════════════════════════════════════════════════════════════ */

const symbolLegendy = (r, symbol, cx, cy) => {
  const prost = (w, h) => [[cx - w / 2, cy - h / 2], [cx + w / 2, cy - h / 2], [cx + w / 2, cy + h / 2], [cx - w / 2, cy + h / 2]]
  if (symbol === 'modul') rysujModul(r, prost(5, 7.2), { nr: 1, rama: 0.35 })
  else if (symbol === 'komin') rysujKomin(r, prost(5.5, 5.5))
  else if (symbol === 'okno_dachowe') rysujOkno(r, prost(5, 6.4))
  else if (symbol === 'pod_dachem') rysujPodDachem(r, prost(9, 5.5))
  else if (symbol === 'strefa') rysujStrefe(r, prost(9, 5.5))
  else if (symbol === 'odstep') rysujOdstep(r, prost(9, 5.5))
  else if (symbol === 'spadek') {
    rysujStrzalke(r, cx - 4.5, cy, [1, 0], 6)
    r.tekst('38°', cx + 2.5, cy - 0.9, { rozmiar: ROZMIAR.maly })
  } else if (symbol === 'kosz') {
    r.linia(cx - 5, cy + 2.5, cx + 5, cy - 2.5, { grubosc: 0.25 })
  } else if (PASY.includes(symbol)) rysujPas(r, symbol, prost(10, 2.4), 1.6)
  else if (symbol === 'lukarna') {
    const w = prost(6, 6)
    rysujPrzeszkodeRodzaju(r, 'lukarna', w, { linie: [[[cx, cy - 3], [cx, cy + 3]], [[cx - 3, cy + 3], [cx, cy - 3]], [[cx + 3, cy + 3], [cx, cy - 3]]] })
  } else rysujPrzeszkodeRodzaju(r, symbol, prost(6, 5))
}

/**
 * Legenda z nagłówkiem „LEGENDA” jak u właściciela: kolumna symboli i opisy. `pozycje` = [{ symbol, opis }]
 * albo [{ rysuj(r, cx, cy), opis }] dla symbolu spoza tego pliku (arkusze instalacji).
 * Zwraca dolną krawędź legendy w mm.
 */
export const rysujLegende = (r, x, y, w, pozycje) => {
  const naglowek = 8
  const kolumna = 15
  r.tekst('LEGENDA', x + w / 2, y + 5.8, { rozmiar: ROZMIAR.naglowek, wyrownanie: 'srodek' })
  let yy = y + naglowek
  r.linia(x, yy, x + w, yy, { grubosc: 0.18 })
  for (const poz of pozycje) {
    const linie = r.zawin(poz.opis, ROZMIAR.opis, w - kolumna - 3)
    const hr = Math.max(9, linie.length * LINIA_OPISU + 3.4)
    if (poz.rysuj) poz.rysuj(r, x + kolumna / 2, yy + hr / 2)
    else symbolLegendy(r, poz.symbol, x + kolumna / 2, yy + hr / 2)
    const y0 = yy + (hr - linie.length * LINIA_OPISU) / 2 + 2.2
    linie.forEach((l, i) => r.tekst(l, x + kolumna + 1.6, y0 + i * LINIA_OPISU, { rozmiar: ROZMIAR.opis }))
    yy += hr
    r.linia(x, yy, x + w, yy, { grubosc: 0.18 })
  }
  r.prostokat(x, y, w, yy - y, { grubosc: 0.35 })
  r.linia(x + kolumna, y + naglowek, x + kolumna, yy, { grubosc: 0.18 })
  return yy
}

/** Blok tekstu (dane instalacji) z nagłówkiem; zwraca dolną krawędź. Wiersze, które się nie mieszczą, pomija. */
export const rysujOpis = (r, x, y, w, maksY, naglowek, wiersze) => {
  let yy = y + 4.2
  r.tekst(naglowek, x + 1.2, yy, { rozmiar: ROZMIAR.wartosc })
  yy += 1.6
  for (const w0 of wiersze) {
    const linie = r.zawin(w0, ROZMIAR.opis, w - 2.4)
    if (yy + linie.length * LINIA_OPISU + 1 > maksY) break
    for (const l of linie) {
      yy += LINIA_OPISU
      r.tekst(l, x + 1.2, yy, { rozmiar: ROZMIAR.opis })
    }
    yy += 0.6
  }
  return yy
}

/* ═══ Łańcuch wymiarów ═════════════════════════════════════════════════════════════════════════════ */

/**
 * Łańcuch z `rozkladLancucha` (`wymiary.js`). `os` 'x': linia pozioma na wysokości `polozenie`, 'y': pionowa na
 * x = `polozenie`. `naPapier(m)` daje położenie punktu wzdłuż linii (to samo co przy rozkładzie). `strona` -1:
 * napisy nad linią (przy 'y' na lewo), +1: pod linią (na prawo). Na końcach i w punktach ukośne kreski.
 */
export const rysujLancuch = (r, { os, polozenie, naPapier, rozklad, strona = -1 }) => {
  const p = rozklad.punkty.map(naPapier)
  if (p.length < 2) return
  const a = Math.min(...p) - 2
  const b = Math.max(...p) + 2
  const { wysokosc: h, odstep } = TEKST_WYMIARU
  const pozioma = os === 'x'
  if (pozioma) r.linia(a, polozenie, b, polozenie, { grubosc: 0.13 })
  else r.linia(polozenie, a, polozenie, b, { grubosc: 0.13 })
  for (const q of p) {
    if (pozioma) r.linia(q - 1.1, polozenie + 1.1, q + 1.1, polozenie - 1.1, { grubosc: 0.25 })
    else r.linia(polozenie - 1.1, q + 1.1, polozenie + 1.1, q - 1.1, { grubosc: 0.25 })
  }
  for (const o of rozklad.odcinki) {
    const odl = odstep + o.poziom * (h + odstep)
    // Linia bazowa: po stronie `strona`; bliższa krawędź napisu o `odl` od linii wymiarowej.
    const baza = strona < 0 ? polozenie - odl : polozenie + odl + h
    if (pozioma) r.tekst(o.tekst, o.x, baza, { rozmiar: ROZMIAR.wymiar, wyrownanie: 'srodek' })
    else r.tekst(o.tekst, baza, o.x, { rozmiar: ROZMIAR.wymiar, wyrownanie: 'srodek', obrot: 90 })
    if (o.poziom) {
      // Odnośnik od środka odcinka do uniesionego napisu.
      const sr = (naPapier(o.od) + naPapier(o.do)) / 2
      const bliska = strona < 0 ? polozenie - odl + 0.3 : polozenie + odl - 0.3
      if (pozioma) r.linia(sr, polozenie, o.x, bliska, { grubosc: 0.08 })
      else r.linia(polozenie, sr, bliska, o.x, { grubosc: 0.08 })
    }
  }
}
